import { useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { apiError } from "@/lib/api";
import { Panel, Labeled, inputCls, SaveButton, NamedList } from "./AdminUI";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function useDraft(settings, onSave, keys) {
  const pick = () => Object.fromEntries(keys.map((k) => [k, settings[k]]));
  const [d, setD] = useState(pick);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await onSave(d);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };
  return [d, (k, v) => setD({ ...d, [k]: v }), save, saving];
}

const NumberField = ({ label, value, onChange, testid }) => (
  <Labeled label={label}><input type="number" min="0" className={inputCls} value={value} onChange={(e) => onChange(Number(e.target.value))} data-testid={testid} /></Labeled>
);

export const AvailabilityPanel = ({ settings, onSave }) => {
  const [d, set, save, saving] = useDraft(settings, onSave,
    ["business_start", "business_end", "weekdays", "slot_interval", "travel_buffer", "blocked_dates", "min_advance_hours", "max_per_day", "max_days_ahead", "timezone", "auto_notify"]);
  const [block, setBlock] = useState("");
  return (
    <Panel title="Service availability" action={<SaveButton onClick={save} saving={saving} testid="availability-save" />}>
      <div className="grid gap-4 sm:grid-cols-3">
        <Labeled label="Opens"><input type="time" className={inputCls} value={d.business_start} onChange={(e) => set("business_start", e.target.value)} data-testid="set-start" /></Labeled>
        <Labeled label="Closes"><input type="time" className={inputCls} value={d.business_end} onChange={(e) => set("business_end", e.target.value)} data-testid="set-end" /></Labeled>
        <Labeled label="Timezone"><input className={inputCls} value={d.timezone} onChange={(e) => set("timezone", e.target.value)} /></Labeled>
        <NumberField label="Slot interval (min)" value={d.slot_interval} onChange={(v) => set("slot_interval", v)} testid="set-interval" />
        <NumberField label="Travel buffer (min)" value={d.travel_buffer} onChange={(v) => set("travel_buffer", v)} testid="set-buffer" />
        <NumberField label="Min advance booking (hours)" value={d.min_advance_hours} onChange={(v) => set("min_advance_hours", v)} testid="set-advance" />
        <NumberField label="Max appointments per day" value={d.max_per_day} onChange={(v) => set("max_per_day", v)} testid="set-max-day" />
        <NumberField label="Book up to (days ahead)" value={d.max_days_ahead} onChange={(v) => set("max_days_ahead", v)} testid="set-days-ahead" />
      </div>
      <p className="mb-2 mt-6 text-xs font-medium uppercase tracking-wide text-slate-500">Available weekdays</p>
      <div className="flex flex-wrap gap-2">
        {DAYS.map((name, i) => {
          const on = d.weekdays.includes(i);
          return (
            <button key={name} type="button" data-testid={`set-weekday-${i}`} onClick={() => set("weekdays", on ? d.weekdays.filter((x) => x !== i) : [...d.weekdays, i].sort())}
              className={`rounded-full px-4 py-1.5 text-sm ${on ? "bg-[#0B132B] text-white" : "bg-[#F5F3EC] text-slate-500"}`}>{name}</button>
          );
        })}
      </div>
      <p className="mb-2 mt-6 text-xs font-medium uppercase tracking-wide text-slate-500">Blocked dates</p>
      <div className="flex gap-2">
        <input type="date" className={`${inputCls} w-48`} value={block} onChange={(e) => setBlock(e.target.value)} data-testid="set-block-date" />
        <button type="button" disabled={!block} onClick={() => { set("blocked_dates", [...new Set([...d.blocked_dates, block])].sort()); setBlock(""); }}
          data-testid="set-block-add" className="rounded-full border border-slate-300 px-4 text-sm">Block</button>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {d.blocked_dates.map((b) => (
          <span key={b} className="inline-flex items-center gap-1 rounded-full bg-red-50 px-3 py-1 text-xs text-red-700">{b}
            <button type="button" onClick={() => set("blocked_dates", d.blocked_dates.filter((x) => x !== b))}><X className="h-3 w-3" /></button></span>
        ))}
      </div>
      <label className="mt-6 flex items-center gap-2 text-sm text-slate-600">
        <input type="checkbox" checked={d.auto_notify} onChange={(e) => set("auto_notify", e.target.checked)} data-testid="set-auto-notify" />
        Email customers automatically when I change a booking status (default)
      </label>
    </Panel>
  );
};

export const PricingVarsPanel = ({ settings, onSave }) => {
  const [d, set, save, saving] = useDraft(settings, onSave, ["property_types", "per_unit_fee", "additional_services", "special_conditions"]);
  return (
    <Panel title="Pricing variables" action={<SaveButton onClick={save} saving={saving} testid="pricing-vars-save" />}>
      <p className="mb-5 text-sm text-slate-500">Optional surcharges added to every estimate. Leave at $0 to not charge.</p>
      <div className="grid gap-8 lg:grid-cols-2">
        <div><p className="mb-2 text-sm font-semibold">Property type surcharges</p>
          <NamedList rows={d.property_types} onChange={(v) => set("property_types", v)} amountKey="surcharge" amountLabel="$" testid="pt-list" /></div>
        <div><p className="mb-2 text-sm font-semibold">Fee per additional unit</p>
          <input type="number" min="0" className={`${inputCls} w-32`} value={d.per_unit_fee} onChange={(e) => set("per_unit_fee", Number(e.target.value))} data-testid="per-unit-fee" /></div>
        <div><p className="mb-2 text-sm font-semibold">Additional services (customer can add)</p>
          <NamedList rows={d.additional_services} onChange={(v) => set("additional_services", v)} amountKey="price" amountLabel="$" testid="addon-list" /></div>
        <div><p className="mb-2 text-sm font-semibold">Special property conditions</p>
          <NamedList rows={d.special_conditions} onChange={(v) => set("special_conditions", v)} amountKey="surcharge" amountLabel="$" testid="cond-list" /></div>
      </div>
    </Panel>
  );
};

export const AreasPanel = ({ settings, onSave }) => {
  const [d, set, save, saving] = useDraft(settings, onSave, ["service_areas"]);
  const upd = (i, k, v) => set("service_areas", d.service_areas.map((a, j) => (j === i ? { ...a, [k]: v } : a)));
  return (
    <Panel title="Service areas & travel fees" action={<SaveButton onClick={save} saving={saving} testid="areas-save" />}>
      <p className="mb-4 text-sm text-slate-500">Requests are accepted for ZIP codes, cities or counties listed here, or within the mileage radius of a center ZIP (Pennsylvania only). Separate ZIP codes with commas or spaces.</p>
      <div className="space-y-4">
        {d.service_areas.map((a, i) => (
          <div key={i} className="rounded-xl border border-slate-200 p-4" data-testid={`area-${i}`}>
            <div className="flex flex-wrap gap-3">
              <input className={`${inputCls} flex-1`} value={a.name} onChange={(e) => upd(i, "name", e.target.value)} data-testid={`area-name-${i}`} />
              <div className="flex items-center gap-1 text-sm">Travel fee $<input type="number" min="0" className={`${inputCls} w-24`} value={a.travel_fee} onChange={(e) => upd(i, "travel_fee", Number(e.target.value))} data-testid={`area-fee-${i}`} /></div>
              <button type="button" className="text-sm text-red-500" onClick={() => set("service_areas", d.service_areas.filter((_, j) => j !== i))}>Remove</button>
            </div>
            <textarea rows={2} className={`${inputCls} mt-3 font-mono-x text-xs`} value={Array.isArray(a.zips) ? a.zips.join(", ") : a.zips}
              onChange={(e) => upd(i, "zips", e.target.value.split(/[\s,]+/))} data-testid={`area-zips-${i}`} />
            <p className="mt-1 text-xs text-slate-400">{(Array.isArray(a.zips) ? a.zips : []).filter(Boolean).length} ZIP codes</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-4">
              <Labeled label="Cities (comma separated)" className="sm:col-span-2"><input className={inputCls} value={(a.cities || []).join(", ")} onChange={(e) => upd(i, "cities", e.target.value.split(","))} data-testid={`area-cities-${i}`} /></Labeled>
              <Labeled label="Counties (comma separated)" className="sm:col-span-2"><input className={inputCls} value={(a.counties || []).join(", ")} onChange={(e) => upd(i, "counties", e.target.value.split(","))} data-testid={`area-counties-${i}`} /></Labeled>
              <Labeled label="Radius center ZIP"><input className={inputCls} value={a.radius_center_zip || ""} onChange={(e) => upd(i, "radius_center_zip", e.target.value)} data-testid={`area-radius-zip-${i}`} /></Labeled>
              <Labeled label="Radius (miles)"><input type="number" min="0" className={inputCls} value={a.radius_miles || 0} onChange={(e) => upd(i, "radius_miles", Number(e.target.value))} data-testid={`area-radius-miles-${i}`} /></Labeled>
            </div>
          </div>
        ))}
      </div>
      <button type="button" className="mt-4 text-sm font-semibold text-gold" data-testid="area-add" onClick={() => set("service_areas", [...d.service_areas, { name: "New area", travel_fee: 0, zips: [] }])}>+ Add area</button>
    </Panel>
  );
};

export const TemplatesPanel = ({ settings, onSave }) => {
  const [d, set, save, saving] = useDraft(settings, onSave, ["templates"]);
  const upd = (k, f, v) => set("templates", { ...d.templates, [k]: { ...d.templates[k], [f]: v } });
  return (
    <Panel title="Email templates" action={<SaveButton onClick={save} saving={saving} testid="templates-save" />}>
      <p className="mb-4 text-sm text-slate-500">Placeholders: {"{name} {full_name} {reference} {service} {date} {time} {address} {total} {phone} {email}"}. Separate paragraphs with a blank line.</p>
      <div className="space-y-5">
        {Object.entries(d.templates).map(([k, t]) => (
          <div key={k} className="rounded-xl border border-slate-200 p-4" data-testid={`template-${k}`}>
            <div className="flex items-center justify-between">
              <p className="font-semibold text-[#0B132B]">{t.label}</p>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={t.enabled} onChange={(e) => upd(k, "enabled", e.target.checked)} data-testid={`template-enabled-${k}`} /> Enabled</label>
            </div>
            <input className={`${inputCls} mt-3`} value={t.subject} onChange={(e) => upd(k, "subject", e.target.value)} data-testid={`template-subject-${k}`} />
            <textarea rows={6} className={`${inputCls} mt-2`} value={t.body} onChange={(e) => upd(k, "body", e.target.value)} data-testid={`template-body-${k}`} />
          </div>
        ))}
      </div>
    </Panel>
  );
};
